import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../../api/adminApi";
import Modal from "../../../components/common/modals/Modal";
import { useNotifications } from "../../../context/NotificationContext";
import { useAuth } from "../../../context/AuthContext";

import {
  Users,
  Search,
  AlertTriangle,
  UserCheck,
  UserX,
  Trash2,
  Building2,
  Eye,
  Clock,
  ExternalLink,
  MoreHorizontal,
  X,
  RefreshCw,
  KeyRound,
  UserRound,
  Mail,
  Terminal,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { toast } from "react-toastify";
import ClientDeletionRequests from "./ClientDeletionRequests";
import "./ClientList.css";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "suspended", label: "Suspended" },
];

const getInitials = (client) => {
  const source = client.BusinessName || client.FullName || client.Email || "?";
  return source
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const formatDate = (value, month = "short") =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        year: "numeric",
        month,
        day: "numeric",
      })
    : "N/A";

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "Never";

const getStoreDomainUrl = (value) => {
  const domain = String(value || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .split("/")[0];
  return /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?(?::\d{1,5})?$/i.test(domain)
    ? `https://${domain}`
    : null;
};

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deletingClientId, setDeletingClientId] = useState(null);
  const [openActionsClientId, setOpenActionsClientId] = useState(null);
  const [updatingSecurityClientId, setUpdatingSecurityClientId] =
    useState(null);
  const [sendingWelcomeEmailClientId, setSendingWelcomeEmailClientId] =
    useState(null);

  const [clientToDelete, setClientToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedClientDetails, setSelectedClientDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [storeAssignment, setStoreAssignment] = useState(null);
  const [availableStores, setAvailableStores] = useState([]);
  const [selectedStoreRequestId, setSelectedStoreRequestId] = useState("");
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [creatingTenant, setCreatingTenant] = useState(false);
  const [tenantMetadataOpen, setTenantMetadataOpen] = useState(false);
  const [tenantBusinessEmail, setTenantBusinessEmail] = useState("");
  const [tenantBusinessPhone, setTenantBusinessPhone] = useState("");
  const [tenantMetaTitle, setTenantMetaTitle] = useState("");
  const [tenantMetaDescription, setTenantMetaDescription] = useState("");
  const [loadingStoreAssignment, setLoadingStoreAssignment] = useState(false);
  const [savingStoreAssignment, setSavingStoreAssignment] = useState(false);
  const [storeAssignmentError, setStoreAssignmentError] = useState("");
  const [deployingNginxSite, setDeployingNginxSite] = useState(false);
  const [nginxDeployError, setNginxDeployError] = useState("");
  const [nginxDeployProgress, setNginxDeployProgress] = useState([]);
  const [migratingTenantUser, setMigratingTenantUser] = useState(false);
  const [showTenantUserMigrationModal, setShowTenantUserMigrationModal] =
    useState(false);
  const [tenantUserMigrationMessage, setTenantUserMigrationMessage] =
    useState("");
  const [tenantUserMigrationError, setTenantUserMigrationError] = useState("");
  const storeAssignmentRequestId = useRef(0);
  const [clientToResetPassword, setClientToResetPassword] = useState(null);
  const [showTemporaryPasswordModal, setShowTemporaryPasswordModal] =
    useState(false);
  const [sendingTemporaryPassword, setSendingTemporaryPassword] =
    useState(false);
  const [clientToSupport, setClientToSupport] = useState(null);
  const [supportReason, setSupportReason] = useState("");
  const [startingSupportSession, setStartingSupportSession] = useState(false);

  const [error, setError] = useState("");
  const { socket } = useNotifications();
  const { beginSupportSession } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!openActionsClientId) return undefined;

    const handlePointerDown = (event) => {
      if (!event.target.closest?.("[data-client-actions]")) {
        setOpenActionsClientId(null);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpenActionsClientId(null);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openActionsClientId]);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await adminApi.getClients();
      const nextClients = response.data || [];
      setClients(nextClients);
      setSelectedClientDetails((current) =>
        current
          ? nextClients.find((client) => client.UserID === current.UserID) ||
            current
          : current,
      );
    } catch (err) {
      console.error("Error fetching clients:", err);
      setError(
        "Couldn't load the client list. Check your connection and try again.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStoreAssignment = useCallback(async (userId) => {
    const requestId = ++storeAssignmentRequestId.current;
    setLoadingStoreAssignment(true);
    setStoreAssignmentError("");
    try {
      const response = await adminApi.getClientStoreAssignment(userId);
      if (requestId === storeAssignmentRequestId.current) {
        setStoreAssignment(response.data);
        setAvailableStores(response.data.availableStores || []);
        const requests = response.data.storeRequests || [];
        setSelectedStoreRequestId(
          (current) =>
            requests.find(
              (storeRequest) =>
                storeRequest.StoreRequestID === current &&
                !storeRequest.StoreTenantID,
            )?.StoreRequestID ||
            requests.find((storeRequest) => !storeRequest.StoreTenantID)
              ?.StoreRequestID ||
            requests[0]?.StoreRequestID ||
            "",
        );
      }
    } catch (err) {
      if (requestId === storeAssignmentRequestId.current) {
        setStoreAssignmentError(
          err.response?.data?.error || "Could not load store assignment options.",
        );
      }
    } finally {
      if (requestId === storeAssignmentRequestId.current) {
        setLoadingStoreAssignment(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchClients();
    if (!socket) return undefined;
    socket.on("platform:updated", fetchClients);
    return () => socket.off("platform:updated", fetchClients);
  }, [fetchClients, socket]);

  useEffect(() => {
    if (!socket) return undefined;
    const handleCommunicationNotification = (notification) => {
      if (
        ["client_email_opt_out", "client_deletion_request"].includes(
          notification?.Type,
        )
      ) {
        fetchClients();
      }
    };
    socket.on("notification:new", handleCommunicationNotification);
    socket.on("client-communications:updated", fetchClients);
    return () => {
      socket.off("notification:new", handleCommunicationNotification);
      socket.off("client-communications:updated", fetchClients);
    };
  }, [fetchClients, socket]);

  const openDetailsModal = (client) => {
    setSelectedClientDetails(client);
    setStoreAssignment(null);
    setAvailableStores([]);
    setSelectedStoreRequestId("");
    setSelectedTenantId("");
    setStoreAssignmentError("");
    setNginxDeployError("");
    setNginxDeployProgress([]);
    setTenantUserMigrationMessage("");
    setTenantMetadataOpen(false);
    setTenantBusinessEmail("");
    setTenantBusinessPhone("");
    setTenantMetaTitle("");
    setTenantMetaDescription("");
    setShowDetailsModal(true);
    loadStoreAssignment(client.UserID);
  };

  const assignStoreToClient = async () => {
    if (
      !selectedClientDetails ||
      !selectedStoreRequestId ||
      !selectedTenantId
    ) return;
    setSavingStoreAssignment(true);
    setStoreAssignmentError("");
    try {
      const response = await adminApi.assignStoreToClient(
        selectedClientDetails.UserID,
        selectedStoreRequestId,
        selectedTenantId,
      );
      setStoreAssignment(response.data);
      setSelectedTenantId("");
      setClients((current) =>
        current.map((client) =>
          client.UserID === selectedClientDetails.UserID
            ? {
                ...client,
                HasAssignedStore: response.data.storeRequests.some(
                  (storeRequest) => storeRequest.StoreTenantID,
                ),
              }
            : client,
        ),
      );
      toast.success("Store assigned to client.");
    } catch (err) {
      const message =
        err.response?.data?.error || "Could not assign this store.";
      setStoreAssignmentError(message);
      toast.error(message);
    } finally {
      setSavingStoreAssignment(false);
    }
  };

  const createTenantForRequest = async () => {
    if (!selectedClientDetails || !selectedStoreRequest) return;
    setCreatingTenant(true);
    setStoreAssignmentError("");
    try {
      const response = await adminApi.createTenantForStoreRequest(
        selectedClientDetails.UserID,
        selectedStoreRequest.StoreRequestID,
        {
          businessEmail: tenantBusinessEmail,
          businessPhone: tenantBusinessPhone,
          metaTitle: tenantMetaTitle,
          metaDescription: tenantMetaDescription,
        },
      );
      setStoreAssignment(response.data);
      setAvailableStores(response.data.availableStores || []);
      setTenantMetadataOpen(false);
      toast.success(response.message || "Tenant created and assigned.");
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.message ||
        "Could not create a tenant for this request.";
      setStoreAssignmentError(message);
      toast.error(message);
    } finally {
      setCreatingTenant(false);
    }
  };

  const deployNginxSite = async () => {
    if (!selectedClientDetails || !selectedStoreRequest) return;
    const domain =
      selectedStoreRequest.ExistingDomain || selectedStoreRequest.StoreUrl;
    if (
      !window.confirm(
        `Create or update the Nginx site for ${domain || "this store request"}? This validates the Nginx configuration and reloads Nginx on the configured server.`,
      )
    ) {
      return;
    }

    setDeployingNginxSite(true);
    setNginxDeployError("");
    setNginxDeployProgress([
      { step: "start", message: "Starting deployment checks." },
    ]);
    try {
      const response = await adminApi.streamStoreRequestNginxDeployment(
        selectedClientDetails.UserID,
        selectedStoreRequest.StoreRequestID,
        (progress) =>
          setNginxDeployProgress((current) => [...current, progress]),
      );
      setNginxDeployProgress((current) => [
        ...current,
        { step: "complete", message: response.message || "Nginx site enabled." },
      ]);
      toast.success(response.message || "Nginx site enabled.");
    } catch (deployError) {
      const message =
        deployError.response?.data?.error ||
        deployError.message ||
        "Could not create the Nginx site.";
      setNginxDeployError(message);
      setNginxDeployProgress((current) => [
        ...current,
        { step: "error", message },
      ]);
      toast.error(message);
    } finally {
      setDeployingNginxSite(false);
    }
  };

  const migrateClientUserToTenant = async () => {
    if (!selectedClientDetails || !selectedStoreRequest?.StoreTenantID) return;
    setShowTenantUserMigrationModal(true);
  };

  const confirmTenantUserMigration = async () => {
    if (!selectedClientDetails || !selectedStoreRequest?.StoreTenantID) return;
    setMigratingTenantUser(true);
    setStoreAssignmentError("");
    setTenantUserMigrationMessage("");
    setTenantUserMigrationError("");
    try {
      const response = await adminApi.migrateClientUserToTenant(
        selectedClientDetails.UserID,
        selectedStoreRequest.StoreRequestID,
      );
      const alreadyMigrated =
        response.data?.status === "already_migrated";
      const message = alreadyMigrated
        ? "This client login is already present in the tenant."
        : "Client login copied to the tenant.";
      setTenantUserMigrationMessage(message);
      setShowTenantUserMigrationModal(false);
      toast.success(message);
    } catch (migrationError) {
      const message =
        migrationError.response?.data?.error ||
        migrationError.message ||
        "Could not copy the client user to the tenant.";
      setStoreAssignmentError(message);
      setTenantUserMigrationError(message);
      toast.error(message);
    } finally {
      setMigratingTenantUser(false);
    }
  };

  const promptDeleteClient = (e, client) => {
    e.stopPropagation();
    setOpenActionsClientId(null);
    setClientToDelete(client);
    setShowDeleteModal(true);
  };

  const confirmDeleteClient = async () => {
    if (!clientToDelete) return;

    try {
      setError("");
      setDeletingClientId(clientToDelete.UserID);
      await adminApi.deleteClient(clientToDelete.UserID);
      setClients((currentClients) =>
        currentClients.filter((item) => item.UserID !== clientToDelete.UserID),
      );
      setShowDeleteModal(false);
      setClientToDelete(null);
    } catch (deleteError) {
      setShowDeleteModal(false);
      setError(
        deleteError.response?.data?.error ||
          "Couldn't delete this client. Try again.",
      );
    } finally {
      setDeletingClientId(null);
    }
  };

  const handleToggleStatus = async (e, client) => {
    e.stopPropagation();
    setOpenActionsClientId(null);
    try {
      const newStatus = !client.IsActive;
      await adminApi.toggleUserStatus(client.UserID, newStatus);
      setClients((prevClients) =>
        prevClients.map((item) =>
          item.UserID === client.UserID
            ? { ...item, IsActive: newStatus }
            : item,
        ),
      );
      if (selectedClientDetails?.UserID === client.UserID) {
        setSelectedClientDetails((prev) => ({ ...prev, IsActive: newStatus }));
      }
    } catch (err) {
      setError("Couldn't update the client's status. Try again.");
    }
  };

  const updateClientInState = (clientId, updates) => {
    setClients((current) =>
      current.map((client) =>
        client.UserID === clientId ? { ...client, ...updates } : client,
      ),
    );
    setSelectedClientDetails((current) =>
      current?.UserID === clientId ? { ...current, ...updates } : current,
    );
  };

  const verifyClientEmail = async (client) => {
    setUpdatingSecurityClientId(client.UserID);
    setError("");
    try {
      const response = await adminApi.verifyClientEmail(client.UserID);
      updateClientInState(client.UserID, response.data);
      toast.success(response.message || "Client email marked as verified.");
    } catch (verifyError) {
      setError(
        verifyError.response?.data?.error ||
          "Could not verify this client's email.",
      );
    } finally {
      setUpdatingSecurityClientId(null);
    }
  };

  const resendClientWelcomeEmail = async (client) => {
    setSendingWelcomeEmailClientId(client.UserID);
    setError("");
    try {
      const response = await adminApi.resendClientWelcomeEmail(client.UserID);
      toast.success(response.message || `Welcome email sent to ${client.Email}.`);
    } catch (sendError) {
      toast.error(
        sendError.response?.data?.error ||
          "Could not resend the welcome email to this client.",
      );
    } finally {
      setSendingWelcomeEmailClientId(null);
    }
  };

  const togglePasswordChangeRequirement = async (client) => {
    const mustChangePassword = !Boolean(client.MustChangePassword);
    setUpdatingSecurityClientId(client.UserID);
    setError("");
    try {
      const response = await adminApi.updateClientSecurityStatus(
        client.UserID,
        mustChangePassword,
      );
      updateClientInState(client.UserID, response.data);
      toast.success(response.message || "Password-change requirement updated.");
    } catch (statusError) {
      setError(
        statusError.response?.data?.error ||
          "Could not update this client's password-change requirement.",
      );
    } finally {
      setUpdatingSecurityClientId(null);
    }
  };

  const promptTemporaryPassword = (event, client) => {
    event?.stopPropagation();
    setOpenActionsClientId(null);
    setShowDetailsModal(false);
    setClientToResetPassword(client);
    setShowTemporaryPasswordModal(true);
  };

  const sendTemporaryPassword = async () => {
    if (!clientToResetPassword) return;
    setSendingTemporaryPassword(true);
    setError("");
    try {
      const response = await adminApi.sendClientTemporaryPassword(
        clientToResetPassword.UserID,
      );
      toast.success(
        response.message ||
          `A temporary password was sent to ${clientToResetPassword.Email}.`,
      );
      setShowTemporaryPasswordModal(false);
      setClientToResetPassword(null);
    } catch (resetError) {
      setShowTemporaryPasswordModal(false);
      setError(
        resetError.response?.data?.error ||
          "Could not send a temporary password to this client.",
      );
    } finally {
      setSendingTemporaryPassword(false);
    }
  };

  const promptSupportSession = (event, client) => {
    event.stopPropagation();
    setOpenActionsClientId(null);
    setSupportReason("");
    setClientToSupport(client);
  };

  const startSupportSession = async (event) => {
    event.preventDefault();
    if (!clientToSupport || !supportReason.trim()) return;

    setStartingSupportSession(true);
    setError("");
    try {
      const response = await adminApi.startClientSupportSession(
        clientToSupport.UserID,
        supportReason.trim(),
      );
      beginSupportSession(response.data);
      setClientToSupport(null);
      navigate("/dashboard", { replace: true });
    } catch (supportError) {
      setError(
        supportError.response?.data?.error ||
          supportError.message ||
          "Could not start the client support session.",
      );
    } finally {
      setStartingSupportSession(false);
    }
  };

  const stats = useMemo(() => {
    const active = clients.filter((c) => c.IsActive).length;
    return {
      total: clients.length,
      active,
      suspended: clients.length - active,
      optionalEmailSubscribers: clients.filter(
        (client) => client.OptionalEmailUpdatesEnabled,
      ).length,
      pendingDeletionRequests: clients.filter(
        (client) => client.DeletionRequestStatus === "pending",
      ).length,
    };
  }, [clients]);

  const filteredClients = useMemo(() => {
    const term = search.trim().toLowerCase();
    return clients.filter((client) => {
      if (statusFilter === "active" && !client.IsActive) return false;
      if (statusFilter === "suspended" && client.IsActive) return false;
      if (!term) return true;
      return (
        client.BusinessName?.toLowerCase().includes(term) ||
        client.Email?.toLowerCase().includes(term) ||
        client.FullName?.toLowerCase().includes(term)
      );
    });
  }, [clients, search, statusFilter]);

  const hasActiveFilters = search.trim() !== "" || statusFilter !== "all";
  const storeRequests = storeAssignment?.storeRequests || [];
  const selectedStoreRequest = storeRequests.find(
    (request) => request.StoreRequestID === selectedStoreRequestId,
  );
  const assignedStore = selectedStoreRequest?.assignedStore;
  const assignedDomain = assignedStore?.Domain || assignedStore?.Subdomain;
  const assignedDomainUrl = getStoreDomainUrl(assignedDomain);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
  };

  return (
    <div className="admin-client-list-page">
      <header className="page-header">
        <div className="header-text">
          <h2>Clients</h2>
          <p className="header-description">
            Manage client accounts, assign store tenants, and track onboarding.
          </p>
        </div>
        <button
          type="button"
          className="btn-ghost"
          onClick={fetchClients}
          disabled={loading}
        >
          <RefreshCw size={15} className={loading ? "spin" : ""} />
          Refresh
        </button>
      </header>

      {error && (
        <div className="alert alert-error" role="alert">
          <AlertTriangle size={18} />
          <span>{error}</span>
          <button
            type="button"
            className="alert-close"
            aria-label="Dismiss error"
            onClick={() => setError("")}
          >
            <X size={16} />
          </button>
        </div>
      )}
      {(selectedStoreRequest?.ExistingDomain ||
        selectedStoreRequest?.StoreUrl) && (
        <div>
          <span className="meta-label">Store domain</span>
          <p className="meta-value">
            {selectedStoreRequest.ExistingDomain ||
              selectedStoreRequest.StoreUrl}
          </p>
        </div>
      )}

      <section className="stats-row" aria-label="Client summary">
        <div className="stat-card">
          <span className="stat-label">Total clients</span>
          <strong className="stat-value">{stats.total}</strong>
        </div>
        <div className="stat-card">
          <span className="stat-label">Active</span>
          <strong className="stat-value tone-success">{stats.active}</strong>
        </div>
        <div className="stat-card">
          <span className="stat-label">Suspended</span>
          <strong className="stat-value tone-danger">{stats.suspended}</strong>
        </div>
        <div className="stat-card">
          <span className="stat-label">Optional email subscribers</span>
          <strong className="stat-value">{stats.optionalEmailSubscribers}</strong>
        </div>
        <div className="stat-card">
          <span className="stat-label">Deletion requests to review</span>
          <strong className="stat-value tone-danger">
            {stats.pendingDeletionRequests}
          </strong>
        </div>
      </section>

      <ClientDeletionRequests />

      <div className="dashboard-card table-card">
        <div className="toolbar">
          <div className="input-icon-wrapper">
            <Search className="search-icon" size={17} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by business, owner, or email"
              className="search-input"
              aria-label="Search clients"
            />
            {search && (
              <button
                type="button"
                className="search-clear"
                aria-label="Clear search"
                onClick={() => setSearch("")}
              >
                <X size={15} />
              </button>
            )}
          </div>

          <div className="segmented" role="group" aria-label="Filter by status">
            {FILTERS.map((filter) => (
              <button
                key={filter.key}
                type="button"
                className={statusFilter === filter.key ? "is-selected" : ""}
                aria-pressed={statusFilter === filter.key}
                onClick={() => setStatusFilter(filter.key)}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div
            className="skeleton-list"
            aria-busy="true"
            aria-label="Loading clients"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div className="skeleton-row" key={i}>
                <span className="skeleton skeleton-avatar" />
                <span className="skeleton skeleton-line" />
                <span className="skeleton skeleton-line short" />
              </div>
            ))}
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="empty-state">
            <Users size={32} className="empty-icon" />
            <h3>
              {hasActiveFilters ? "No matching clients" : "No clients yet"}
            </h3>
            <p>
              {hasActiveFilters
                ? "Try a different search term or status filter."
                : "Clients will appear here once they register."}
            </p>
            {hasActiveFilters && (
              <button
                type="button"
                className="btn-secondary"
                onClick={clearFilters}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="clients-table">
                <thead>
                  <tr>
                    <th className="col-business">Business</th>
                    <th className="col-email">Email</th>
                    <th className="col-phone hide-compact">Phone</th>
                    <th className="col-status">Status</th>
                    <th className="col-registered hide-compact">Registered</th>
                    <th className="col-last-login">Last login</th>
                    <th className="col-email-verified">Email verified</th>
                    <th className="col-communications hide-compact">
                      Communications
                    </th>
                    <th className="col-actions text-right">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <tr
                      key={client.UserID}
                      className={`clickable-row ${
                        openActionsClientId === client.UserID ? "menu-open" : ""
                      }`}
                      tabIndex={0}
                      onClick={() => openDetailsModal(client)}
                      onKeyDown={(e) => {
                        if (e.target === e.currentTarget && e.key === "Enter") {
                          openDetailsModal(client);
                        }
                      }}
                    >
                      <td data-label="Business">
                        <div className="business-details-cell">
                          <div className="client-avatar" aria-hidden="true">
                            {getInitials(client)}
                          </div>
                          <div className="client-names">
                            <span
                              className="business-name"
                              title={
                                client.BusinessName || "Unregistered business"
                              }
                            >
                              {client.BusinessName || "Unregistered business"}
                            </span>
                            <span className="owner-name">
                              {client.FullName || "N/A"}
                            </span>
                            <span
                              className={`store-assignment-label ${
                                client.HasAssignedStore
                                  ? "is-assigned"
                                  : "is-unassigned"
                              }`}
                            >
                              {client.HasAssignedStore
                                ? "Store assigned"
                                : "No store linked"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td data-label="Email">
                        <span className="text-ellipsis" title={client.Email}>
                          {client.Email}
                        </span>
                      </td>
                      <td className="hide-compact" data-label="Phone">
                        {client.MobileNumber || "N/A"}
                      </td>
                      <td data-label="Status">
                        <span
                          className={`status-pill ${
                            client.IsActive ? "status-live" : "status-suspended"
                          }`}
                        >
                          <span className="dot" />
                          {client.IsActive ? "Active" : "Suspended"}
                        </span>
                      </td>
                      <td
                        className="date-cell hide-compact"
                        data-label="Registered"
                      >
                        {formatDate(client.CreatedAt)}
                      </td>
                      <td className="date-cell" data-label="Last login">
                        {formatDateTime(client.LastLogin)}
                      </td>
                      <td data-label="Email verified">
                        {Boolean(client.EmailVerified) ? "Yes" : "No"}
                      </td>
                      <td
                        className="hide-compact communication-status-cell"
                        data-label="Communications"
                      >
                        <span
                          className={`status-pill ${
                            client.OptionalEmailUpdatesEnabled
                              ? "status-live"
                              : "status-suspended"
                          }`}
                          title={
                            client.OptionalEmailUpdatesEnabled
                              ? `Opted in${client.ConsentRecordedAt ? ` on ${formatDateTime(client.ConsentRecordedAt)}` : ""}`
                              : `Opted out or not subscribed${client.WithdrawnAt ? ` on ${formatDateTime(client.WithdrawnAt)}` : ""}`
                          }
                        >
                          {client.OptionalEmailUpdatesEnabled
                            ? "Updates on"
                            : "Updates off"}
                        </span>
                        {client.DeletionRequestStatus && (
                          <span
                            className={`status-pill ${
                              client.DeletionRequestStatus === "pending"
                                ? "status-pending"
                                : "status-in_progress"
                            }`}
                            title={
                              client.DeletionRequestedAt
                                ? `Requested ${formatDateTime(client.DeletionRequestedAt)}`
                                : ""
                            }
                          >
                            Deletion {client.DeletionRequestStatus}
                          </span>
                        )}
                      </td>
                      <td
                        className="text-right actions-cell"
                        data-label="Actions"
                      >
                        <div
                          className={`client-actions-menu ${
                            openActionsClientId === client.UserID
                              ? "is-open"
                              : ""
                          }`}
                          data-client-actions
                        >
                          <button
                            type="button"
                            className="client-actions-trigger"
                            aria-label={`More actions for ${client.BusinessName || client.FullName || "client"}`}
                            aria-haspopup="menu"
                            aria-expanded={
                              openActionsClientId === client.UserID
                            }
                            title="More actions"
                            onClick={(event) => {
                              event.stopPropagation();
                              setOpenActionsClientId((currentId) =>
                                currentId === client.UserID
                                  ? null
                                  : client.UserID,
                              );
                            }}
                          >
                            <MoreHorizontal size={18} />
                          </button>

                          {openActionsClientId === client.UserID && (
                            <div className="client-actions-popup" role="menu">
                              <button
                                type="button"
                                role="menuitem"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setOpenActionsClientId(null);
                                  openDetailsModal(client);
                                }}
                              >
                                <Eye size={15} />
                                View details
                              </button>
                              <button
                                type="button"
                                role="menuitem"
                                disabled={deletingClientId === client.UserID}
                                onClick={(event) =>
                                  handleToggleStatus(event, client)
                                }
                              >
                                {client.IsActive ? (
                                  <UserX size={15} />
                                ) : (
                                  <UserCheck size={15} />
                                )}
                                {client.IsActive
                                  ? "Suspend client"
                                  : "Activate client"}
                              </button>
                              <button
                                type="button"
                                role="menuitem"
                                disabled={!client.IsActive}
                                onClick={(event) =>
                                  promptSupportSession(event, client)
                                }
                              >
                                <UserRound size={15} />
                                Support as client
                              </button>
                              <button
                                type="button"
                                role="menuitem"
                                onClick={(event) =>
                                  promptTemporaryPassword(event, client)
                                }
                              >
                                <KeyRound size={15} />
                                Send temporary password
                              </button>
                              <button
                                type="button"
                                role="menuitem"
                                className="delete-menu-item"
                                disabled={deletingClientId === client.UserID}
                                onClick={(event) =>
                                  promptDeleteClient(event, client)
                                }
                              >
                                <Trash2 size={15} />
                                Delete client
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <footer className="table-footer">
              Showing {filteredClients.length} of {clients.length} clients
            </footer>
          </>
        )}
      </div>

      {showDetailsModal && selectedClientDetails && (
        <Modal
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          title="Client details"
        >
          <div className="client-details-modal">
            <div className="details-header-card">
              <div className="details-header-icon">
                <Building2 size={22} />
              </div>
              <div className="details-header-info">
                <h3>
                  {selectedClientDetails.BusinessName || "Unnamed business"}
                </h3>
                <p>{selectedClientDetails.FullName || "N/A"}</p>
              </div>
              <span
                className={`status-pill ${
                  selectedClientDetails.IsActive
                    ? "status-live"
                    : "status-suspended"
                }`}
              >
                <span className="dot" />
                {selectedClientDetails.IsActive ? "Active" : "Suspended"}
              </span>
            </div>

            <section className="details-section">
              <h4 className="section-title">Contact</h4>
              <dl className="info-grid">
                <div className="info-box">
                  <dt className="info-label">Email</dt>
                  <dd className="info-val">
                    {selectedClientDetails.Email || "N/A"}
                  </dd>
                </div>
                <div className="info-box">
                  <dt className="info-label">Phone</dt>
                  <dd className="info-val">
                    {selectedClientDetails.MobileNumber || "N/A"}
                  </dd>
                </div>
                <div className="info-box">
                  <dt className="info-label">Registered</dt>
                  <dd className="info-val">
                    {formatDate(selectedClientDetails.CreatedAt, "long")}
                  </dd>
                </div>
                <div className="info-box">
                  <dt className="info-label">Last login</dt>
                  <dd className="info-val">
                    {formatDateTime(selectedClientDetails.LastLogin)}
                  </dd>
                </div>
                <div className="info-box">
                  <dt className="info-label">Account ID</dt>
                  <dd className="info-val code-font">
                    #{selectedClientDetails.UserID}
                  </dd>
                </div>
              </dl>
            </section>

            <section className="details-section">
              <h4 className="section-title">Optional email updates</h4>
              <dl className="info-grid">
                <div className="info-box">
                  <dt className="info-label">Mailing preference</dt>
                  <dd className="info-val">
                    {selectedClientDetails.OptionalEmailUpdatesEnabled
                      ? "Opted in"
                      : "Opted out / not subscribed"}
                  </dd>
                </div>
                <div className="info-box">
                  <dt className="info-label">Preference last updated</dt>
                  <dd className="info-val">
                    {formatDateTime(
                      selectedClientDetails.OptionalEmailUpdatesEnabled
                        ? selectedClientDetails.ConsentRecordedAt
                        : selectedClientDetails.WithdrawnAt,
                    )}
                  </dd>
                </div>
                <div className="info-box">
                  <dt className="info-label">Preference source</dt>
                  <dd className="info-val">
                    {selectedClientDetails.ConsentSource || "Legacy default"}
                  </dd>
                </div>
                <div className="info-box">
                  <dt className="info-label">Account deletion request</dt>
                  <dd className="info-val">
                    {selectedClientDetails.DeletionRequestStatus
                      ? `${selectedClientDetails.DeletionRequestStatus} · ${formatDateTime(selectedClientDetails.DeletionRequestedAt)}`
                      : "No open request"}
                  </dd>
                </div>
              </dl>
              <p className="account-security-description">
                Opted-out clients are automatically excluded from optional
                emails sent through this platform. Essential service emails
                remain enabled.
              </p>
            </section>

            <section className="details-section">
              <h4 className="section-title">Account security</h4>
              <dl className="info-grid security-status-grid">
                <div className="info-box">
                  <dt className="info-label">Email verified</dt>
                  <dd className="info-val">
                    {Boolean(selectedClientDetails.EmailVerified)
                      ? "Yes"
                      : "No"}
                  </dd>
                </div>
                <div className="info-box">
                  <dt className="info-label">Verification token</dt>
                  <dd className="info-val">
                    {Boolean(selectedClientDetails.HasEmailVerificationToken)
                      ? "Pending (value hidden)"
                      : "None"}
                  </dd>
                </div>
                <div className="info-box">
                  <dt className="info-label">Verification token expiry</dt>
                  <dd className="info-val">
                    {selectedClientDetails.EmailVerificationExpires
                      ? formatDateTime(
                          selectedClientDetails.EmailVerificationExpires,
                        )
                      : "N/A"}
                  </dd>
                </div>
                <div className="info-box">
                  <dt className="info-label">Must change password</dt>
                  <dd className="info-val">
                    {Boolean(selectedClientDetails.MustChangePassword)
                      ? "Yes"
                      : "No"}
                  </dd>
                </div>
              </dl>
              <p className="account-security-description">
                Verification token values are kept private. Verifying the client
                clears any pending token and expiry.
              </p>
              <div className="security-actions">
                {!Boolean(selectedClientDetails.EmailVerified) && (
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={
                      updatingSecurityClientId === selectedClientDetails.UserID
                    }
                    onClick={() => verifyClientEmail(selectedClientDetails)}
                  >
                    {updatingSecurityClientId === selectedClientDetails.UserID
                      ? "Updating..."
                      : "Verify client"}
                  </button>
                )}
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={
                  sendingWelcomeEmailClientId ===
                  selectedClientDetails.UserID
                  }
                  onClick={() =>
                  resendClientWelcomeEmail(selectedClientDetails)
                  }
                >
                  <Mail size={15} />
                  {sendingWelcomeEmailClientId === selectedClientDetails.UserID
                  ? "Sending..."
                  : "Resend welcome email"}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={
                    updatingSecurityClientId === selectedClientDetails.UserID
                  }
                  onClick={() =>
                    togglePasswordChangeRequirement(selectedClientDetails)
                  }
                >
                  {Boolean(selectedClientDetails.MustChangePassword)
                    ? "Remove password-change requirement"
                    : "Require password change"}
                </button>
              </div>
              <p className="account-security-description">
                Send a temporary sign-in password to this client. They must
                choose a new password before continuing.
              </p>
              <button
                type="button"
                className="btn-secondary"
                onClick={(event) =>
                  promptTemporaryPassword(event, selectedClientDetails)
                }
              >
                <KeyRound size={15} />
                Send temporary password
              </button>
            </section>

            <section className="details-section">
              <div className="request-meta-row">
                <h4 className="section-title">Store assignment</h4>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => navigate("/admin/nginx-server")}
                >
                  Configure Nginx server
                </button>
              </div>

              {loadingStoreAssignment ? (
                <div className="no-request-box" aria-busy="true">
                  <Clock size={22} />
                  <p>Loading store assignment options…</p>
                </div>
              ) : storeAssignmentError ? (
                <div className="no-request-box" role="alert">
                  <p>{storeAssignmentError}</p>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() =>
                      loadStoreAssignment(selectedClientDetails.UserID)
                    }
                  >
                    Retry
                  </button>
                </div>
              ) : !storeAssignment?.configured ? (
                <div className="no-request-box">
                  <p>Configure the store database before assigning a store.</p>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => navigate("/admin/store-database")}
                  >
                    Configure store database
                  </button>
                </div>
              ) : storeRequests.length === 0 ? (
                <div className="no-request-box">
                  <p>
                    This client has no store requests. Store tenants can only be
                    assigned to a specific store request.
                  </p>
                </div>
              ) : (
                <div className="store-request-box">
                  <label className="info-label" htmlFor="client-store-request">
                    Store request
                  </label>
                  <select
                    id="client-store-request"
                    className="search-input"
                    value={selectedStoreRequestId}
                    onChange={(event) => {
                      setSelectedStoreRequestId(event.target.value);
                      setSelectedTenantId("");
                      setTenantUserMigrationMessage("");
                      setNginxDeployError("");
                      setNginxDeployProgress([]);
                      const request = storeRequests.find(
                        (item) => item.StoreRequestID === event.target.value,
                      );
                      const businessName =
                        request?.BusinessName ||
                        selectedClientDetails?.BusinessName ||
                        "";
                      setTenantMetaTitle(
                        businessName ? `${businessName} | Online Store` : "",
                      );
                      setTenantMetaDescription(
                        request?.BusinessDescription ||
                          request?.ClientBusinessDescription ||
                          (businessName
                            ? `Shop online at ${businessName}.`
                            : ""),
                      );
                      setTenantBusinessEmail(request?.BusinessEmail || "");
                      setTenantBusinessPhone("");
                      setTenantMetadataOpen(false);
                    }}
                    disabled={savingStoreAssignment}
                  >
                    {storeRequests.map((request) => (
                      <option
                        key={request.StoreRequestID}
                        value={request.StoreRequestID}
                      >
                        {request.RequestNumber || request.StoreRequestID} —{" "}
                        {request.BusinessName} ({request.Status})
                      </option>
                    ))}
                  </select>
                  {selectedStoreRequest && (
                    <>
                      <div className="request-meta-row">
                        <div>
                          <span className="meta-label">Request business</span>
                          <p className="meta-value">
                            {selectedStoreRequest.BusinessName}
                          </p>
                        </div>
                        <div>
                          <span className="meta-label">Request status</span>
                          <p className="meta-value">
                            {selectedStoreRequest.Status}
                          </p>
                        </div>
                        {selectedStoreRequest.StoreUrl && (
                          <div>
                            <span className="meta-label">Existing store URL</span>
                            <p className="meta-value">
                              {selectedStoreRequest.StoreUrl}
                            </p>
                          </div>
                        )}
                      </div>
                      {assignedStore ? (
                        <>
                          {assignedDomain && (
                            <div className="store-url-row">
                              <span className="meta-label">Tenant domain</span>
                              {assignedDomainUrl ? (
                                <a
                                  href={assignedDomainUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="store-url-link"
                                >
                                  {assignedDomain}
                                  <ExternalLink size={14} />
                                </a>
                              ) : (
                                <span className="meta-value">
                                  {assignedDomain}
                                </span>
                              )}
                            </div>
                          )}
                          <div className="request-meta-row">
                            <div>
                              <span className="meta-label">
                                Connected store
                              </span>
                              <p className="meta-value">
                                {assignedStore.BusinessName}
                              </p>
                            </div>
                            <div>
                              <span className="meta-label">Tenant code</span>
                              <p className="meta-value code-font">
                                {assignedStore.TenantCode}
                              </p>
                            </div>
                            <div>
                              <span className="meta-label">Store status</span>
                              <p className="meta-value">
                                {assignedStore.SetupStatus ||
                                  assignedStore.SubscriptionStatus ||
                                  (assignedStore.IsActive
                                    ? "Active"
                                    : "Inactive")}
                              </p>
                            </div>
                          </div>
                          <p className="account-security-description">
                            Assigned{" "}
                            {formatDateTime(
                              selectedStoreRequest.StoreTenantAssignedAt,
                            )}
                          </p>
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={deployNginxSite}
                            disabled={deployingNginxSite}
                          >
                            {deployingNginxSite
                              ? "Applying Nginx site…"
                              : "Create / update Nginx site"}
                          </button>
                          {nginxDeployProgress.length > 0 && (
                            <section
                              className="nginx-deploy-console"
                              aria-label="Nginx deployment progress"
                              aria-live="polite"
                            >
                              <div className="nginx-deploy-console-header">
                                <span>
                                  <Terminal size={16} aria-hidden="true" />
                                  Deployment progress
                                </span>
                                <span>
                                  {deployingNginxSite
                                    ? "RUNNING"
                                    : nginxDeployError
                                      ? "FAILED"
                                      : "FINISHED"}
                                </span>
                              </div>
                              <div className="nginx-deploy-console-body">
                                {nginxDeployProgress.map((item, index) => (
                                  <div
                                    className={`nginx-deploy-console-line ${
                                      item.step === "error"
                                        ? "is-error"
                                        : item.step === "complete"
                                          ? "is-success"
                                          : ""
                                    }`}
                                    key={`${item.step}-${index}`}
                                  >
                                    {item.step === "error" ? (
                                      <span aria-hidden="true">×</span>
                                    ) : item.step === "complete" ? (
                                      <CheckCircle2
                                        size={14}
                                        aria-hidden="true"
                                      />
                                    ) : (
                                      <span aria-hidden="true">›</span>
                                    )}
                                    <span>{item.message}</span>
                                  </div>
                                ))}
                                {deployingNginxSite && (
                                  <div className="nginx-deploy-console-line is-active">
                                    <Loader2
                                      size={14}
                                      className="spin"
                                      aria-hidden="true"
                                    />
                                    <span>Waiting for the server…</span>
                                  </div>
                                )}
                              </div>
                              <p className="nginx-deploy-console-note">
                                If either certificate file is missing, the
                                helper requests a Let&apos;s Encrypt certificate
                                for the apex and www domains before applying
                                the site. DNS must resolve to this server.
                              </p>
                            </section>
                          )}
                        </>
                      ) : selectedStoreRequest.StoreTenantID ? (
                        <p className="account-security-description">
                          Tenant ID{" "}
                          <span className="code-font">
                            {selectedStoreRequest.StoreTenantID}
                          </span>{" "}
                          is linked, but its details could not be loaded from
                          the connected database.
                        </p>
                      ) : (
                        <>
                          {String(selectedStoreRequest.Status).toLowerCase() ===
                            "approved" && (
                            <div className="tenant-create-panel">
                              <p className="account-security-description">
                                Create a new tenant in the connected store
                                database and link it to this request.
                              </p>
                              {!tenantMetadataOpen ? (
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() => {
                                    const businessName =
                                      selectedStoreRequest.BusinessName ||
                                      selectedClientDetails?.BusinessName ||
                                      "";
                                    setTenantMetaTitle(
                                      `${businessName} | Online Store`,
                                    );
                                    setTenantMetaDescription(
                                      selectedStoreRequest.BusinessDescription ||
                                        selectedStoreRequest.ClientBusinessDescription ||
                                        `Shop online at ${businessName}.`,
                                    );
                                    setTenantBusinessEmail(
                                      selectedStoreRequest.BusinessEmail || "",
                                    );
                                    setTenantBusinessPhone("");
                                    setTenantMetadataOpen(true);
                                  }}
                                  disabled={creatingTenant}
                                >
                                  Create tenant for this request
                                </button>
                              ) : (
                                <div className="tenant-create-fields">
                                  <label
                                    className="info-label"
                                    htmlFor="tenant-business-email"
                                  >
                                    Store business email
                                  </label>
                                  <input
                                    id="tenant-business-email"
                                    className="search-input"
                                    type="email"
                                    required
                                    value={tenantBusinessEmail}
                                    onChange={(event) =>
                                      setTenantBusinessEmail(event.target.value)
                                    }
                                  />
                                  <label
                                    className="info-label"
                                    htmlFor="tenant-business-phone"
                                  >
                                    Store business phone (optional)
                                  </label>
                                  <input
                                    id="tenant-business-phone"
                                    className="search-input"
                                    type="tel"
                                    maxLength={20}
                                    value={tenantBusinessPhone}
                                    onChange={(event) =>
                                      setTenantBusinessPhone(event.target.value)
                                    }
                                  />
                                  <label
                                    className="info-label"
                                    htmlFor="tenant-meta-title"
                                  >
                                    SEO title
                                  </label>
                                  <input
                                    id="tenant-meta-title"
                                    className="search-input"
                                    value={tenantMetaTitle}
                                    maxLength={255}
                                    onChange={(event) =>
                                      setTenantMetaTitle(event.target.value)
                                    }
                                  />
                                  <label
                                    className="info-label"
                                    htmlFor="tenant-meta-description"
                                  >
                                    SEO description
                                  </label>
                                  <textarea
                                    id="tenant-meta-description"
                                    className="search-input"
                                    rows={3}
                                    maxLength={1000}
                                    value={tenantMetaDescription}
                                    onChange={(event) =>
                                      setTenantMetaDescription(
                                        event.target.value,
                                      )
                                    }
                                  />
                                  <p className="account-security-description">
                                    Tenant name, domain, business email, and
                                    description come from the approved request
                                    and client profile. Plan defaults to Basic;
                                    other store content can be configured later.
                                  </p>
                                  <div className="tenant-create-actions">
                                    <button
                                      type="button"
                                      className="btn-primary"
                                      onClick={createTenantForRequest}
                                      disabled={
                                        creatingTenant ||
                                        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                                          tenantBusinessEmail.trim(),
                                        ) ||
                                        !tenantMetaTitle.trim() ||
                                        !tenantMetaDescription.trim()
                                      }
                                    >
                                      {creatingTenant
                                        ? "Creating tenant…"
                                        : "Create and assign tenant"}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-secondary"
                                      onClick={() =>
                                        setTenantMetadataOpen(false)
                                      }
                                      disabled={creatingTenant}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          <label
                            className="info-label"
                            htmlFor="client-store-tenant"
                          >
                            Choose a store tenant for this request
                          </label>
                          <select
                            id="client-store-tenant"
                            className="search-input"
                            value={selectedTenantId}
                            onChange={(event) =>
                              setSelectedTenantId(event.target.value)
                            }
                            disabled={savingStoreAssignment}
                          >
                            <option value="">Select a store tenant</option>
                            {availableStores.map((store) => (
                              <option
                                key={store.TenantID}
                                value={store.TenantID}
                              >
                                {store.BusinessName} —{" "}
                                {store.Domain ||
                                  store.Subdomain ||
                                  store.TenantCode}
                              </option>
                            ))}
                          </select>
                          {availableStores.length === 0 && (
                            <p className="account-security-description">
                              No active store tenants are available.
                            </p>
                          )}
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={assignStoreToClient}
                            disabled={
                              !selectedTenantId || savingStoreAssignment
                            }
                          >
                            {savingStoreAssignment
                              ? "Assigning…"
                              : "Assign store tenant"}
                          </button>
                        </>
                      )}
                      {selectedStoreRequest.StoreTenantID && (
                        <div className="tenant-user-migration">
                          <p className="account-security-description">
                            Copy available account details to this tenant:
                            name, email, phone, password hash, role, account
                            status, and available verification/reset data.
                          </p>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={migrateClientUserToTenant}
                            disabled={
                              migratingTenantUser || loadingStoreAssignment
                            }
                          >
                            {migratingTenantUser ? (
                              <>
                                <Loader2 size={16} className="spin" />
                                Copying user…
                              </>
                            ) : (
                              <>
                                <UserRound size={16} />
                                Copy user to tenant
                              </>
                            )}
                          </button>
                          {tenantUserMigrationMessage && (
                            <p className="tenant-user-migration-status" role="status">
                              {tenantUserMigrationMessage}
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </section>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      <Modal
        isOpen={showTenantUserMigrationModal}
        onClose={() => {
          if (!migratingTenantUser) setShowTenantUserMigrationModal(false);
        }}
        title="Copy client user to tenant"
        size="sm"
        closeOnOverlayClick={!migratingTenantUser}
      >
        <div className="tenant-user-migration-confirmation">
          {tenantUserMigrationError && (
            <p className="tenant-user-migration-error" role="alert">
              {tenantUserMigrationError}
            </p>
          )}
          <p>
            This will create an <strong>Admin</strong> user in the assigned
            tenant using the client&apos;s available account details and
            existing password hash.
          </p>
          <p>
            Store request:{" "}
            <strong>
              {selectedStoreRequest?.RequestNumber ||
                selectedStoreRequest?.BusinessName ||
                "Selected request"}
            </strong>
          </p>
          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowTenantUserMigrationModal(false)}
              disabled={migratingTenantUser}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={confirmTenantUserMigration}
              disabled={migratingTenantUser || !selectedStoreRequest?.StoreTenantID}
            >
              {migratingTenantUser ? "Copying user..." : "Copy as Admin"}
            </button>
          </div>
        </div>
      </Modal>

      {showTemporaryPasswordModal && clientToResetPassword && (
        <Modal
          isOpen={showTemporaryPasswordModal}
          onClose={() => {
            if (sendingTemporaryPassword) return;
            setShowTemporaryPasswordModal(false);
            setClientToResetPassword(null);
          }}
          title="Send temporary password"
          closeOnOverlayClick={!sendingTemporaryPassword}
        >
          <div className="temporary-password-confirmation">
            <div className="temporary-password-icon">
              <KeyRound size={22} />
            </div>
            <p>
              A new temporary password will be sent to{" "}
              <strong>{clientToResetPassword.Email}</strong>. Their current
              password will stop working, and they will be required to change
              the temporary password after signing in.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowTemporaryPasswordModal(false);
                  setClientToResetPassword(null);
                }}
                disabled={sendingTemporaryPassword}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={sendTemporaryPassword}
                disabled={sendingTemporaryPassword}
              >
                {sendingTemporaryPassword
                  ? "Sending..."
                  : "Send temporary password"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {clientToSupport && (
        <Modal
          isOpen
          onClose={() => setClientToSupport(null)}
          title="Start client support session"
        >
          <form className="support-session-form" onSubmit={startSupportSession}>
            <p>
              You will enter the client portal as{" "}
              <strong>
                {clientToSupport.FullName || clientToSupport.Email}
              </strong>
              . Your admin identity will be shown in a persistent banner, and
              your actions will be recorded in the audit log.
            </p>
            <p>
              The session lasts for the duration configured in Admin Email
              Settings. End it as soon as support is complete.
            </p>
            <label htmlFor="support-session-reason">Support reason</label>
            <textarea
              id="support-session-reason"
              value={supportReason}
              onChange={(event) => setSupportReason(event.target.value)}
              minLength={10}
              maxLength={500}
              rows={3}
              required
              placeholder="Describe the client's support request"
            />
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setClientToSupport(null)}
                disabled={startingSupportSession}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-danger-solid"
                disabled={
                  startingSupportSession || supportReason.trim().length < 10
                }
              >
                {startingSupportSession
                  ? "Starting..."
                  : "Start support session"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showDeleteModal && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete client"
        >
          <div className="delete-modal-content">
            <div className="warning-banner">
              <AlertTriangle size={22} />
              <div>
                <h4>This can't be undone</h4>
                <p>
                  Deleting <strong>{clientToDelete?.FullName}</strong> (
                  {clientToDelete?.Email}) permanently removes their store data,
                  onboarding progress, and requests. A reviewed and approved
                  deletion request is required, and retention obligations must
                  be checked first.
                </p>
              </div>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={deletingClientId !== null}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger-solid"
                onClick={confirmDeleteClient}
                disabled={deletingClientId !== null}
              >
                {deletingClientId !== null ? "Deleting..." : "Delete client"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ClientList;
